import { Popover, PopoverContent, PopoverTrigger } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { type ReactNode, useId } from 'react';
import { useSnapshot } from 'valtio';
import { RequestType } from '~/common/types';
import type { UtilityAgency } from '../common';
import { activityMap } from '../drawers/ActivityFeed/ActivityList';
import store from '../store';
import CellDate from './CellDate';
import { getUtilityRowId, requestMatchesRow } from './grouping';

interface Props {
  agency: UtilityAgency;
}

/**
 * One update line that opens its details on hover, click or Enter. The line is the button, so the details are
 * reachable by keyboard, and it keeps its visible text as its name with `label` as the hint. It opens to the left
 * because the column sits at the table's right edge.
 */
function UpdatePopover({
  label,
  trigger,
  cardClassName = 'w-72',
  'data-testid': dataTestId,
  children,
}: {
  label: string;
  trigger: ReactNode;
  cardClassName?: string;
  'data-testid'?: string;
  children: ReactNode;
}) {
  const hintId = useId();

  return (
    <Popover openOnHover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-describedby={hintId}
          className="w-full min-w-0 cursor-pointer rounded-sm text-left focus-ring"
          onClick={(e) => e.stopPropagation()}
          data-testid={dataTestId}
        >
          {trigger}
        </button>
      </PopoverTrigger>
      {/* Outside the button, so it's read once, as the hint, and not as part of the name. */}
      <span id={hintId} className="sr-only">
        {label}
      </span>
      <PopoverContent side="left" aria-label={label} className="border-0 bg-transparent p-0 shadow-none">
        {/* DetailCard's look without its <dl>: the history is free-form, not label/value rows. */}
        <section
          aria-label="Update history"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll area must be focusable to scroll by keyboard (WCAG 2.1.1).
          tabIndex={0}
          data-testid={dataTestId && `${dataTestId}-details`}
          className={classNames(
            'flex flex-col gap-2 rounded-lg border border-grey-30 bg-white p-3 text-left font-noto text-xs shadow-[0_0_4px_rgba(0,0,0,0.25)] focus-ring',
            cardClassName,
          )}
        >
          {children}
        </section>
      </PopoverContent>
    </Popover>
  );
}

export default function LatestUpdateCell({ agency }: Props) {
  const { dataRequests } = useSnapshot(store) as typeof store;
  // One per source (digital data, each request). The cell shows only the newest; its details list them all.
  const updates: { key: string; date?: DateTime; testId: string; line: ReactNode; history: ReactNode }[] = [];

  if (agency.availableDigitally) {
    updates.push({
      key: 'digital',
      date: agency.lastRefresh,
      testId: `utilities-latest-update-digital-${getUtilityRowId(agency)}`,
      line: (
        <>
          <CellDate date={agency.lastRefresh} />
          [D] Data updated, refreshed {agency.refreshCadence?.toLowerCase()}.
        </>
      ),
      history: (
        <div className="p-2">
          <div className="text-2xs uppercase text-secondary">
            {agency.lastRefresh?.toLocaleString(DateTime.DATE_MED)}
          </div>
          <div className="text-xs">
            Digital data updated by CivilGrid. This data is updated {agency.refreshCadence?.toLowerCase()}.
          </div>
        </div>
      ),
    });
  }

  // look for other updates
  const requests = Object.values(dataRequests).filter((d) => requestMatchesRow(d, agency));
  if (requests.length > 0) {
    requests.forEach((r) => {
      const activities = r.activities.map((a, i) => {
        const name = <span>{a.user?.name || a.user?.email} </span>;
        const Component = activityMap[a.activityType];
        return (
          <div key={a.id} className={classNames({ 'bg-secondary-light/50': i % 2 !== 0 })}>
            {Component && (
              <div className="my-2 mb-8 p-2">
                <div className="text-2xs uppercase text-secondary">{a.createdAt.toLocaleString(DateTime.DATE_MED)}</div>
                <div>
                  {name}
                  <Component activity={a} includeAgencyName={false} />
                </div>
              </div>
            )}
          </div>
        );
      });

      // Update activities show their note as a `Note:` line, so the current note is added only when the latest differs.
      const latestNoteUpdate = r.activities
        .flatMap((a) => (a.details?.updates ?? []) as { key: string; value: string }[])
        .findLast((u) => u.key === 'notes');
      const showNote = r.notes && latestNoteUpdate?.value !== r.notes;

      const mostRecentActivity = r.activities[r.activities.length - 1]; // undefined for demo-copied requests (activities not copied)
      let trigger: ReactNode;
      if (r.notes) {
        trigger = r.notes; // if note exists, always display it.
      } else if (mostRecentActivity) {
        const Component = activityMap[mostRecentActivity.activityType];
        trigger = <Component activity={mostRecentActivity} compact={true} />;
      } else {
        return; // no notes and no activities — nothing to show for this request
      }

      const tag = r.type === RequestType.MapsRequested ? 'M' : 'AB';
      updates.push({
        key: r.id,
        date: mostRecentActivity?.createdAt,
        testId: `utilities-latest-update-${r.id}`,
        line: (
          <>
            <CellDate date={mostRecentActivity?.createdAt} />
            <span className="mr-1">[{tag}]</span>
            {trigger}
          </>
        ),
        history: (
          <>
            <div className="px-2 pt-2 text-xs font-medium">
              [{tag}] {tag === 'M' ? 'Map' : 'As-Built'} request
            </div>
            {showNote && <div className="whitespace-pre-wrap break-words px-2 pt-2 text-xs">{r.notes}</div>}
            {activities.reverse()}
            <div className="p-2 font-mono text-xs">[Ref: {r.requestId}]</div>
          </>
        ),
      });
    });
  }

  if (updates.length === 0) return null;
  updates.sort((a, b) => (b.date?.toMillis() ?? 0) - (a.date?.toMillis() ?? 0));
  const [newest] = updates;

  return (
    <UpdatePopover
      label={`Update history for ${agency.agencyName}`}
      cardClassName="w-96 max-h-72 overflow-y-auto"
      trigger={<span className="line-clamp-2">{newest.line}</span>}
      data-testid={newest.testId}
    >
      {updates.map((u) => (
        <div key={u.key}>{u.history}</div>
      ))}
    </UpdatePopover>
  );
}
